/**
 * Runtime grey recolor for the map's "Unsure" pin (no personal TasteMatch,
 * CD decision 2026-10-04). There is no grey no-data art, so the existing
 * yellow `00_noDataPin` body is converted in place: every opaque pixel's
 * luminance is rescaled so the pin's yellow body lands on `PIN_GREY` while
 * the dark outline stays dark and alpha (including anti-aliased edges) is
 * untouched. `greyscaleNoDataPin` is pure and DOM-free so it can be unit tested
 * without a canvas; `greyscaleImage` is the one canvas wrapper around it.
 */

/** The yellow body color of `00_noDataPin.png`, sampled from the asset. */
const NO_DATA_YELLOW = { r: 255, g: 253, b: 87 } as const;

/** The flat grey body of the Unsure pin (166 of 255: the body of the retired `*PublicPin` art, 166,166,165). */
export const PIN_GREY = 166;

const luma = (r: number, g: number, b: number) => 0.299 * r + 0.587 * g + 0.114 * b;

const GREY_SCALE = PIN_GREY / luma(NO_DATA_YELLOW.r, NO_DATA_YELLOW.g, NO_DATA_YELLOW.b);

/** Converts RGBA pixel data (as from `getImageData`) to grey in place, keeping alpha. */
export function greyscaleNoDataPin(data: Uint8ClampedArray): void {
  for (let i = 0; i < data.length; i += 4) {
    const grey = Math.min(255, Math.round(luma(data[i], data[i + 1], data[i + 2]) * GREY_SCALE));
    data[i] = grey;
    data[i + 1] = grey;
    data[i + 2] = grey;
  }
}

/**
 * Draws `img` onto a new canvas and converts it to grey with
 * `greyscaleNoDataPin`. Returns the canvas, or `null` when a 2D context is
 * unavailable. Pixel access can still throw (e.g. a tainted canvas): callers
 * decide whether that rejects (map) or falls back (Pin Key).
 */
export function greyscaleImage(img: HTMLImageElement): HTMLCanvasElement | null {
  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0);
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  greyscaleNoDataPin(imageData.data);
  ctx.putImageData(imageData, 0, 0);
  return canvas;
}

/**
 * Size and position of the 🤔 face on the "Unsure" pins, matched to the real
 * level-face disc on the designed personal pins. Measured from the PNG alpha
 * and color of `04_mediumCompatibilityPin`, `06_mostCompatibilityPin`,
 * `01_noCompatibilityPin` and `04_mediumCompatibilityListPin` (400 x 600/604):
 * the face disc is 330-339 px wide (0.825-0.85 of the pin width) and its
 * center sits at x = 197-199.5 (centered) and y = 193.5-195 (0.321-0.325 of
 * the pin height). Shared by the map compositing (RestaurantMapView) and the
 * Pin Key overlay (MapPinLegend) so the two cannot drift apart.
 *
 * The glyph's visible diameter is taken as ~1x its font size (measured on
 * Apple Color Emoji; other platforms' emoji fonts may render slightly
 * larger or smaller, which has not been checked on a device).
 */
export const FACE_DIAMETER_RATIO = 0.83;
export const FACE_CENTER_Y_RATIO = 0.324;
/**
 * Visual nudge, as a fraction of pin height, added on top of the measured disc
 * center. The glyph's ink sits above the text line's middle, so a face drawn at
 * the measured center looked a couple of pixels high in the pin's empty circle
 * (CD, 2026-10-04). Net nudge is 0.04, about 2px at the map's 45px pin height. Tuned by eye, not measured.
 */
export const FACE_Y_NUDGE_RATIO = 0.04;

/** Font size and center point (same units as the inputs) for the 🤔 on a pin of this size. */
export function getFaceMetrics(pinWidth: number, pinHeight: number) {
  return {
    fontSize: pinWidth * FACE_DIAMETER_RATIO,
    centerX: pinWidth / 2,
    centerY: pinHeight * (FACE_CENTER_Y_RATIO + FACE_Y_NUDGE_RATIO),
  };
}
